import React from "react";

export default function PuckedTerms() {
  const containerStyle = {
    backgroundColor: "#f4f4f5",
    color: "#18181b",
    minHeight: "100vh",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px",
  };

  const headerStyle = {
    width: "100%",
    maxWidth: "800px",
    padding: "20px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const backBtnStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#52525b",
    textDecoration: "none",
    fontSize: "0.95rem",
    fontWeight: "500",
  };

  const articleStyle = {
    width: "100%",
    maxWidth: "800px",
    backgroundColor: "#ffffff",
    border: "1px solid #e4e4e7",
    borderRadius: "16px",
    padding: "40px",
    margin: "20px 0 60px",
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    boxSizing: "border-box",
  };

  const titleStyle = {
    fontSize: "2.2rem",
    fontWeight: "700",
    margin: "0 0 8px",
    color: "#09090b",
    textAlign: "left",
  };

  const dateStyle = {
    fontSize: "0.9rem",
    color: "#71717a",
    marginBottom: "32px",
    textAlign: "left",
  };

  const headingStyle = {
    fontSize: "1.3rem",
    fontWeight: "600",
    marginTop: "32px",
    marginBottom: "12px",
    color: "#09090b",
    textAlign: "left",
  };

  const paragraphStyle = {
    lineHeight: "1.6",
    color: "#27272a",
    marginBottom: "16px",
    textAlign: "left",
  };

  const listStyle = {
    paddingLeft: "20px",
    marginBottom: "24px",
    lineHeight: "1.6",
    color: "#27272a",
    textAlign: "left",
  };

  const listItemStyle = {
    marginBottom: "8px",
  };

  const linkStyle = {
    color: "#d97706",
    textDecoration: "underline",
    fontWeight: "500",
  };

  const contactBoxStyle = {
    backgroundColor: "#fafafa",
    border: "1px solid #e4e4e7",
    borderRadius: "8px",
    padding: "16px 20px",
    marginTop: "16px",
    textAlign: "left",
  };

  const footerStyle = {
    padding: "24px 0",
    color: "#71717a",
    fontSize: "0.85rem",
    marginTop: "auto",
  };

  return (
    <div style={containerStyle}>
      {/* HEADER */}
      <header style={headerStyle}>
        <a href="/pucked" title="Back to Pucked" style={backBtnStyle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          <span>Pucked</span>
        </a>

        <div>
          <span style={{ fontWeight: "600", fontSize: "1.1rem" }}>
            Terms of Use
          </span>
        </div>

        <div style={{ width: "70px" }} />
      </header>

      {/* LEGAL CONTENT */}
      <main
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        <article style={articleStyle}>
          <h1 style={titleStyle}>Terms of Use</h1>
          <p style={dateStyle}>Last updated: July 25, 2026</p>

          <p style={paragraphStyle}>
            Welcome to <strong>Pucked</strong> ("the App"), operated by Tilbo
            Apps ("we", "us", or "our"), based in Belgium. By downloading,
            accessing, or using Pucked, you agree to be bound by these Terms of
            Use ("Terms"). If you do not agree with these Terms, please do not
            use the App.
          </p>

          <p style={paragraphStyle}>
            For iOS users, these Terms incorporate and supplement Apple's
            Licensed Application End User License Agreement (Apple EULA). For
            Android users, these Terms govern your access under Google Play's
            Terms of Service.
          </p>

          <h2 style={headingStyle}>1. Description of Service</h2>
          <p style={paragraphStyle}>
            Pucked is a mobile application designed to assist espresso
            enthusiasts in tracking extractions, logging coffee bean parameters,
            analyzing brew metrics, and receiving automated AI-powered barista
            feedback.
          </p>

          <h2 style={headingStyle}>2. Subscriptions and Payments</h2>
          <p style={paragraphStyle}>
            Pucked offers auto-renewable premium subscriptions ("Pucked Pro") on
            a <strong>weekly</strong>, <strong>monthly</strong>, or{" "}
            <strong>annual</strong> basis.
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>No Free Trial:</strong> Subscriptions take effect
              immediately upon purchase confirmation through your Apple ID or
              Google Play account.
            </li>
            <li style={listItemStyle}>
              <strong>Auto-Renewal:</strong> Subscriptions automatically renew
              unless auto-renew is turned off at least 24 hours before the end
              of the current billing period.
            </li>
            <li style={listItemStyle}>
              <strong>Payment & Billing:</strong> Your Apple ID or Google Play
              account will be charged for renewal within 24 hours prior to the
              end of the current period at the rate of the chosen plan.
            </li>
            <li style={listItemStyle}>
              <strong>Cancellation & Refunds:</strong> You can manage and cancel
              your subscriptions in your App Store or Google Play Store account
              settings after purchase. All payment processing, billing, and
              refund requests are handled exclusively by Apple or Google in
              accordance with their store policies.
            </li>
          </ul>

          <h2 style={headingStyle}>
            3. AI Barista Feedback & Content Disclaimer
          </h2>
          <p style={paragraphStyle}>
            The App includes features that generate automated brewing
            recommendations, grind adjustments, and extraction diagnostics using
            artificial intelligence (Google Gemini API).
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              <strong>Informational Purposes Only:</strong> AI recommendations
              are generated for informational, educational, and entertainment
              purposes only.
            </li>
            <li style={listItemStyle}>
              <strong>No Guarantee:</strong> We do not guarantee that AI
              suggestions will yield specific taste outcomes or prevent
              extraction flaws.
            </li>
            <li style={listItemStyle}>
              <strong>Equipment Safety:</strong> You are solely responsible for
              operating your coffee machinery, grinders, and equipment safely.
              We are not responsible for any damage to your equipment, property,
              or personal injuries resulting from relying on AI recommendations
              or adjustments.
            </li>
          </ul>

          <h2 style={headingStyle}>4. Intellectual Property</h2>
          <p style={paragraphStyle}>
            All rights, title, and interest in and to Pucked, including its
            software code, UI design, branding, logos, graphics, and text, are
            and will remain the exclusive property of Tilbo Apps. You are
            granted a limited, non-exclusive, non-transferable, revocable
            license to use the App for personal, non-commercial purposes.
          </p>

          <h2 style={headingStyle}>5. Disclaimer of Warranties ("AS IS")</h2>
          <p style={paragraphStyle}>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE APP IS
            PROVIDED ON AN <strong>"AS IS"</strong> AND{" "}
            <strong>"AS AVAILABLE"</strong> BASIS WITHOUT WARRANTIES OF ANY
            KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT
            LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>
          <p style={paragraphStyle}>
            WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE,
            SECURE, ACCURATE, OR THAT DATA LOSS WILL NOT OCCUR.
          </p>

          <h2 style={headingStyle}>6. Limitation of Liability</h2>
          <p style={paragraphStyle}>
            TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL TILBO
            APPS, ITS DEVELOPERS, AFFILIATES, OR SUPPLIERS BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            OR ANY LOSS OF PROFITS, DATA, USE, GOODWILL, OR PROPERTY DAMAGE
            (INCLUDING COFFEE EQUIPMENT OR MACHINERY) RESULTING FROM:
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>
              YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE APP;
            </li>
            <li style={listItemStyle}>
              ANY CONDUCT OR CONTENT OF ANY THIRD PARTY WITHIN THE SERVICE;
            </li>
            <li style={listItemStyle}>
              ANY RECOMMENDATION, DIAGNOSIS, OR OUTPUT GENERATED BY THE AI
              FEATURE;
            </li>
            <li style={listItemStyle}>
              UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR
              CONTENT.
            </li>
          </ul>
          <p style={paragraphStyle}>
            IN NO EVENT SHALL OUR TOTAL CUMULATIVE LIABILITY EXCEED THE TOTAL
            AMOUNT PAID BY YOU TO US FOR THE APP IN THE PAST SIX (6) MONTHS.
          </p>

          <h2 style={headingStyle}>7. Indemnification</h2>
          <p style={paragraphStyle}>
            You agree to defend, indemnify, and hold harmless Tilbo Apps and its
            developers from and against any claims, liabilities, damages,
            judgments, awards, losses, costs, expenses, or fees (including
            reasonable attorneys' fees) arising out of or relating to your
            violation of these Terms or your use of the App.
          </p>

          <h2 style={headingStyle}>8. Governing Law and Jurisdiction</h2>
          <p style={paragraphStyle}>
            These Terms shall be governed by and construed in accordance with
            the laws of <strong>Belgium</strong>, without regard to its conflict
            of law principles. Any legal suit, action, or proceeding arising out
            of or related to these Terms or the App shall be instituted
            exclusively in the competent courts located in Belgium.
          </p>

          <h2 style={headingStyle}>9. Changes to These Terms</h2>
          <p style={paragraphStyle}>
            We reserve the right to modify or replace these Terms at any time.
            We will indicate the "Last updated" date at the top of this
            document. Your continued use of the App following the posting of any
            changes constitutes acceptance of those changes.
          </p>

          <h2 style={headingStyle}>10. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have any questions or concerns regarding these Terms, please
            contact us at:
          </p>
          <div style={contactBoxStyle}>
            <p style={{ margin: "2px 0", fontWeight: "600" }}>Tilbo Apps</p>
            <p style={{ margin: "2px 0", color: "#52525b" }}>
              Location: Belgium
            </p>
            <p style={{ margin: "2px 0" }}>
              Email:{" "}
              <a href="mailto:tilboapps@gmail.com" style={linkStyle}>
                tilboapps@gmail.com
              </a>
            </p>
          </div>
        </article>
      </main>

      {/* FOOTER */}
      <footer style={footerStyle}>
        <p style={{ margin: 0 }}>
          © {new Date().getFullYear()} Tilbo Apps. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
